import React, { useEffect, useRef, useState } from 'react';
import { alpha, Box, Stack } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { HtmlTooltip } from 'components/html-tooltip';
import { useDebouncedCallback } from 'use-debounce';
import { ITileInfoIconProps } from './types';
import { Instance } from '@popperjs/core';
import { IElementData, IElementDataSettings } from 'components/common/element-data/types';
import showdown from 'showdown';
import striptags from 'striptags';
import { useElementMetadataSettings } from 'hooks/useElementMetadata';
import { useSelector } from 'react-redux';
import { useHomeContext } from 'app/home/context';
import { format } from 'date-fns/format';
import ElementDataInfo from 'components/common/element-data/ElementDataInfo';
import isOwnerVisible from 'tools/owners';
import { useAppSelector } from 'store/hooks';
import { UserAuth } from 'store/auth';

const converter = new showdown.Converter();

export const TileInfoIcon = function (props: ITileInfoIconProps) {
    const {
        style,
        defaultStyle = {},
        tile,
        children,
        settings,
        isListView,
        usage = 'info',
        customFieldsDisplayOnTileInfo,
    } = props;
    const [mouseOverInfoIcon, setMouseOverInfoIcon] = useState(false);
    const iconRef = useRef<any | null>(null);
    const popperRef = useRef<Instance>(null);
    const positionRef = useRef<{ x: number; y: number }>({
        x: 0,
        y: 0,
    });
    const isAppPanel = useSelector((state: any) => state.concierge.isAppPanel) as boolean;
    const { configuration }: UserAuth = useAppSelector((state) => state.auth);
    const ctx = useHomeContext();
    const [topicPopupProps] = [ctx.topicPopupProps];

    const debouncedHide = useDebouncedCallback((value) => {
        setMouseOverInfoIcon(value);
    }, 100);

    const setMaxHeight = () => {
        if (popperRef.current) {
            const popperEl = popperRef.current.state?.elements?.popper;
            if (popperEl) {
                const direction = popperRef.current?.state?.styles?.popper?.bottom == '0' ? 'top' : 'bottom';
                const posY = popperRef.current?.state?.rects?.reference?.y ?? 0;
                const windowHeight = document.documentElement.clientHeight;
                const indent = 8;
                let maxHeight = 0;
                if (direction == 'top') {
                    const topBarHeight = 40;
                    maxHeight = posY + indent - topBarHeight;
                } else {
                    maxHeight = windowHeight - posY - indent;
                }

                popperEl.style.maxHeight = maxHeight + 'px';
                popperRef.current.update();
            }
        }
    };
    useEffect(() => {
        if (mouseOverInfoIcon) {
            setTimeout(setMaxHeight, 100);

            if (iconRef?.current) {
                const tileEl = iconRef.current.closest('.tile-element');
                if (tileEl) tileEl.classList.add('state--hover');
            }
        } else {
            if (iconRef?.current) {
                const tileEl = iconRef.current.closest('.tile-element');
                if (tileEl) tileEl.classList.remove('state--hover');
            }
        }
    }, [mouseOverInfoIcon]);

    let metadataSettings = useElementMetadataSettings(usage);
    const elementData: IElementData = {
        tileInfoData: tile,
        elementId: tile.element_id,
        elementType: tile?.element_type,
        userDashboardElementInstanceId: tile.user_dashboard_element_instance_id,
        lastMeasurementValueFormatted: tile.last_measurement_value_formatted,
        lastMeasurementTimeFormatted: tile.last_measurement_time_formatted,
        metricTileDisplayPctVariance: tile.metric_tile_display_pct_variance,
        pctVarianceText: tile.pct_variance_text,
        metricMovingAverageInterval: tile.metric_moving_average_interval,
        movingAverageValue: tile.metric_last_moving_average_value_formatted,
        descriptionMarkdownInd: true,
        descriptionMarkdown: tile.element_info
            ? tile.description_markdown_ind === 'Y'
                ? converter.makeHtml(String(tile.element_info))
                : striptags(String(tile.element_info ?? ''), ['br', 'b', 'a', 'i'])
            : '',
        description: tile.element_info ?? '',
        descriptionMaxRows: 11,
        certifiedInd: tile.certified_ind == 'Y',
        certificationLevelColor: tile.certification_level_color ?? 'success.main',
        certificationLevelName: tile.certification_level_name,
        lastCertifiedByName: tile.last_certified_by_name,
        lastCertifiedTime:
            tile.last_certified_time && '0000-00-00 00:00:00' != tile.last_certified_time
                ? format(new Date(tile.last_certified_time), 'M/dd/yyyy')
                : '',
        lastCertifiedByEmail: tile.last_certified_by_email,
        businessOwner: tile.business_owner,
        businessOwnerEmail: tile.business_owner_email,
        technicalOwner: tile.technical_owner,
        technicalOwnerEmail: tile.technical_owner_email,
        dataSteward: tile.data_steward,
        dataStewardEmail: tile.data_steward_email,
        projected: tile.total_forecast_amount_formatted,
        dataSourceName: tile.data_source_name,
        lastDisplayGenerationTime: tile.last_display_generation_time,
        lastFileUpdatedTime: tile.last_file_updated_time,
        engagement: tile.global_total_view_count,
        documents: tile.documents,
        documentsNeedToLoad: !tile.documents,
        customFields: tile.custom_fields,
        customFieldsNeedToLoad: !tile.custom_fields,
        customFieldDisplay: customFieldsDisplayOnTileInfo,
        topics: tile.topics_info,
        topicsNeedToLoad: !tile.topics_info,
        topicIdsToLoad: tile.topics,
        glossaryTermPopupProps: topicPopupProps,
    };

    if (metadataSettings.fields['thumbnail']?.display) {
        metadataSettings.fields['thumbnail'].display =
            isListView &&
            undefined !== settings?.theme?.catalog_list_column_count &&
            settings?.theme?.catalog_list_column_count > 1;
    }
    if (metadataSettings.fields['last_data_point']?.display) {
        metadataSettings.fields['last_data_point'].display =
            tile.hide_mtf !== 'Y' &&
            !('external report' === tile.element_type && 'level 1' === tile.supports_last_refreshed_check);
    }
    if (metadataSettings.fields['description']?.display) {
        metadataSettings.fields['description'].display =
            tile.element_info &&
            tile.element_dashboard_name !==
                tile.element_info.replaceAll('<p>', '').replaceAll('</p>', '').replaceAll('\n', '');
    }
    if (metadataSettings.fields['engagement']?.display) {
        metadataSettings.fields['engagement'].display =
            tile.has_access !== 'N' && tile?.global_total_view_count !== undefined;
    }

    ['business_owner', 'technical_owner', 'data_steward'].forEach((ownerField) => {
        if (metadataSettings.fields[ownerField]?.display) {
            metadataSettings.fields[ownerField].display =
                isOwnerVisible((configuration.owners as any)[ownerField], 'elements') &&
                isOwnerVisible((configuration.owners as any)[ownerField], 'tile_preview');
        }
    });

    const elementDataSettings: IElementDataSettings = {
        metadata: metadataSettings,
        isAppPanel: isAppPanel,
    };
    return (
        <HtmlTooltip
            placement={'right-start'}
            arrow={false}
            certified={tile.certified_ind === 'Y'}
            sx={{
                display: 'flex',
            }}
            slotProps={{
                tooltip: {
                    sx: {
                        margin: '0 !important',
                        border: '0 !important',
                        boxShadow: 'none !important',
                        background: 'none !important',
                        maxWidth: 'none !important',
                    },
                },
                popper: {
                    sx: {},
                },
            }}
            PopperProps={{
                popperRef,
                anchorEl: {
                    getBoundingClientRect: () => {
                        return new DOMRect(positionRef.current.x, positionRef.current.y, 16, 16);
                    },
                },
            }}
            title={
                mouseOverInfoIcon && (
                    <Box
                        sx={{
                            maxHeight: '100%',
                            px: 1,
                            display: 'flex',
                        }}
                        onMouseLeave={() => {
                            debouncedHide(false);
                        }}
                        onMouseEnter={() => {
                            debouncedHide.cancel();
                            setMouseOverInfoIcon(true);
                        }}
                    >
                        <Stack
                            sx={{
                                width: '280px',
                                lineHeight: 1.5,
                                overflow: 'auto',
                                maxHeight: '100%',
                                border: '1px solid',
                                borderColor: (theme) =>
                                    tile.certified_ind === 'Y'
                                        ? theme.palette.success.main
                                        : theme.palette.primary.main,
                                borderRadius: '3px',
                                boxShadow: (theme) => `0px 1px 4px 0px ${alpha(theme.palette.text.primary, 0.32)}`,
                                backgroundColor: 'background.default',
                            }}
                            onClick={(event) => {
                                //@ts-ignore
                                if (event?.target?.localName != 'a') {
                                    event.preventDefault();
                                }
                                event.stopPropagation();
                            }}
                        >
                            <ElementDataInfo
                                elementData={elementData}
                                elementDataSettings={elementDataSettings}
                                showHeader={elementDataSettings.metadata.settings.showSectionHeaders}
                            />
                        </Stack>
                    </Box>
                )
            }
            open={mouseOverInfoIcon}
        >
            <Box
                ref={iconRef}
                sx={{ ...style, ...defaultStyle }}
                onMouseLeave={() => {
                    debouncedHide(false);
                }}
                onMouseEnter={(event) => {
                    debouncedHide.cancel();
                    const elementCoor = (event.target as HTMLElement).getBoundingClientRect();
                    positionRef.current = { x: elementCoor.left, y: elementCoor.top };
                    setMouseOverInfoIcon(true);
                }}
                className={`tile-element__info ${tile.certified_ind === 'Y' ? 'tile-element__info--certified' : ''}`}
            >
                {tile.certified_ind === 'Y' ? (
                    <Box
                        className={'tile-element__info--certified'}
                        sx={{
                            display: 'flex',
                            color: tile.certification_level_color || 'success.main',
                            fontSize: '16px',
                        }}
                    >
                        <IconMi icon={'certified'} />
                    </Box>
                ) : (
                    <Box
                        className={'tile-element__info--default'}
                        sx={{
                            display: 'flex',
                            color: (theme) => alpha(theme.palette.text.primary, 0.64),
                            fontSize: '16px',
                        }}
                    >
                        <IconMi icon={'info-circle'} />
                    </Box>
                )}
            </Box>
        </HtmlTooltip>
    );
};
